import type { Server } from "../data/types";
import { localizeServerName, t } from "../utils/i18n";

interface Props {
  servers: Server[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  favoriteIds: number[];
  isFavorite: (id: number) => boolean;
  onToggleFavorite: (id: number) => void;
}

const TYPE_LABEL: Record<number, string> = {
  0: t("typeSatellite"),
  1: t("typeMeteor"),
};

/**
 * サーバー名に対応するギリシャ文字のマッピング (Select表示用)
 */
const GREEK_MAP: Record<string, string> = {
  アルファ: "α",
  ベータ: "β",
  ガンマ: "γ",
  デルタ: "δ",
  イプシロン: "ε",
  ゼータ: "ζ",
  イータ: "η",
  シータ: "θ",
  イオタ: "ι",
  カッパ: "κ",
  ラムダ: "λ",
  ミュー: "μ",
  ニュー: "ν",
  クサイ: "ξ",
  オミクロン: "ο",
  パイ: "π",
  ロー: "ρ",
  シグマ: "σ",
  タウ: "τ",
  ユプシロン: "υ",
  ファイ: "φ",
  カイ: "χ",
  プサイ: "ψ",
  オメガ: "ω",
};

export function ServerSelect({
  servers,
  selectedId,
  onSelect,
  favoriteIds,
  isFavorite,
  onToggleFavorite,
}: Props) {
  const grouped = new Map<number, Server[]>();
  for (const s of servers) {
    const arr = grouped.get(s.type) ?? [];
    arr.push(s);
    grouped.set(s.type, arr);
  }
  const types = [...grouped.keys()].sort((a, b) => b - a);

  const favoriteSet = new Set(favoriteIds);
  const favoriteServers = servers
    .filter((s) => favoriteSet.has(s.id))
    .sort((a, b) => b.type - a.type || a.level - b.level);

  const selectedFav = selectedId !== null && isFavorite(selectedId);

  const getLabel = (s: Server) => {
    const greek = GREEK_MAP[s.name];
    const displayName = localizeServerName(s.name);
    return greek ? `Lv${s.level} [${greek}] ${displayName}` : `Lv${s.level} ${displayName}`;
  };

  return (
    <div className="flex items-center gap-2">
      <select
        className="flex-1 rounded border border-slate-300 bg-white px-2 py-2 text-sm"
        value={selectedId ?? ""}
        onChange={(e) => onSelect(Number(e.target.value))}
      >
        {favoriteServers.length > 0 && (
          <optgroup label={t("favorites")}>
            {favoriteServers.map((s) => (
              <option key={`fav-${s.id}`} value={s.id}>
                ★ {getLabel(s)}
              </option>
            ))}
          </optgroup>
        )}
        {types.map((t) => (
          <optgroup key={t} label={TYPE_LABEL[t] ?? `Type ${t}`}>
            {grouped.get(t)!.map((s) => (
              <option key={s.id} value={s.id}>
                {getLabel(s)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <button
        onClick={() => selectedId !== null && onToggleFavorite(selectedId)}
        disabled={selectedId === null}
        aria-label={selectedFav ? t("removeFromFavorites") : t("addToFavorites")}
        aria-pressed={selectedFav}
        className={
          "shrink-0 rounded border px-3 py-2 text-base transition-colors " +
          (selectedFav
            ? "border-yellow-400 bg-yellow-50 text-yellow-600"
            : "border-slate-300 bg-white text-slate-400 hover:text-slate-600")
        }
      >
        {selectedFav ? "★" : "☆"}
      </button>
    </div>
  );
}
